import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, User, Building2, Globe, Star, Ghost, Loader2, ArrowLeftRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Client, DEAL_TYPES, PRIORITY_TIERS } from '@/types/crm';
import { formatDistanceToNow } from 'date-fns';

interface ClientCardProps {
  client: Client;
  onClick?: () => void;
  onQuickMove?: () => void;
  isSaving?: boolean;
  isBulkMode?: boolean;
  isSelected?: boolean;
}

export function ClientCard({ client, onClick, onQuickMove, isSaving, isBulkMode, isSelected }: ClientCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client.id, disabled: isBulkMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.5 : 1,
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const displayValue = client.confirmed_monthly_revenue || client.estimated_monthly_value;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`touch-manipulation select-none hover:shadow-[0_4px_16px_rgba(27,58,45,0.12)] transition-shadow ${
        isBulkMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
      } ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-3 space-y-2">
        {/* Company Name & Tier */}
        <div className="flex items-center justify-between gap-2">
          <div className="font-bold text-sm truncate flex items-center gap-1.5">
            {isBulkMode && (
              <Checkbox
                checked={isSelected}
                // Selection toggles via the card's own onClick; keep the checkbox
                // purely visual so taps on it reach the card handler.
                className="flex-shrink-0 pointer-events-none"
                tabIndex={-1}
              />
            )}
            <Building2 className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            {client.company_name}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {client.is_ghosty && (
              <Ghost className="h-4 w-4 text-muted-foreground" />
            )}
            {client.priority_tier && PRIORITY_TIERS[client.priority_tier] && (
              <Badge variant={PRIORITY_TIERS[client.priority_tier].color as 'destructive' | 'warning' | 'secondary'} className="text-xs shrink-0">
                <Star className="h-3 w-3 mr-0.5" />
                T{client.priority_tier}
              </Badge>
            )}
            {onQuickMove && (
              <button
                type="button"
                aria-label={`Move ${client.company_name} to another stage`}
                className="sm:hidden relative flex size-9 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground after:absolute after:-inset-1.5 after:content-['']"
                onClick={(e) => { e.stopPropagation(); onQuickMove(); }}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <ArrowLeftRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Website */}
        {client.website && (
          <a
            href={client.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-link hover:underline truncate"
            onClick={(e) => e.stopPropagation()}
          >
            <Globe className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{client.website.replace(/^https?:\/\//, '')}</span>
          </a>
        )}

        {/* Contact Name */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{client.primary_contact_name}</span>
        </div>

        {/* Value */}
        {displayValue != null && displayValue > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs font-serif">
              <DollarSign className="h-3 w-3 mr-0.5" />
              {formatCurrency(displayValue)}{client.revenue_type === 'one_off' ? ' one-time' : '/mo'}
            </Badge>
          </div>
        )}

        {/* Deal Type */}
        {client.deal_type && (
          <Badge variant="secondary" className="text-xs">
            {DEAL_TYPES[client.deal_type].label}
          </Badge>
        )}

        {/* Assigned Sales Owner */}
        {client.assigned_sales_owner && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="h-3 w-3 flex-shrink-0" />
            <span>{client.assigned_sales_owner.name}</span>
          </div>
        )}

        {/* Last Activity */}
        {client.last_activity_at && (
          <div className="text-xs text-muted-foreground">
            Last touch: {formatDistanceToNow(new Date(client.last_activity_at), { addSuffix: true })}
          </div>
        )}

        {/* Last Updated */}
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          {isSaving && <Loader2 className="h-3 w-3 animate-spin" />}
          {formatDistanceToNow(new Date(client.updated_at), { addSuffix: true })}
        </div>
      </CardContent>
    </Card>
  );
}
